import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  Heading,
  TimeField,
  DateInput,
  Label,
  DateSegment,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarGridBody,
} from 'react-aria-components';
import {
  parseDate,
  parseTime,
  CalendarDate,
  Time,
  toCalendarDateTime,
} from '@internationalized/date';
import cn from 'classnames';
import Occasion from '@support/Occasion';
import PopoverMenu from '@components/Menus/PopoverMenu';
import { ChevronLeftIcon, ChevronRightIcon, CircledCheckIcon, ClockIcon } from '@components/Icons';
import styles from './DateTimePicker.module.scss';
import { metaTrigger } from '@support/keyboardHandler';
import { useSelector } from 'react-redux';
import { selectDefaultReminderTime } from '@reducers/user/metadata';

export function TimePicker({
  className,
  value,
  onChange,
  label = 'Reminder',
  onKeyDown,
}: {
  className?: string;
  value: Time;
  onChange: (time: Time) => void;
  label?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}) {
  return (
    <TimeField
      className={cn(styles.timeField, className)}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
    >
      <Label>{label} time</Label>
      <div className={styles.timeFieldInput}>
        <ClockIcon className={styles.clockIcon} />
        <DateInput>{(segment) => <DateSegment segment={segment} />}</DateInput>
      </div>
    </TimeField>
  );
}

function PickerMenu({
  dateValue,
  minDate,
  setDate,
  timeValue,
  setTime,
  save,
  disabled,
  label = 'Reminder',
  onKeyDown,
}: {
  dateValue: CalendarDate;
  minDate: CalendarDate;
  setDate: (date: CalendarDate) => void;
  timeValue: Time;
  setTime: (time: Time) => void;
  save: () => void;
  disabled: boolean;
  label?: string;
  onKeyDown: (e: React.KeyboardEvent) => void;
}) {
  return (
    <>
      <Calendar
        aria-label={`${label} date`}
        className={styles.calendar}
        value={dateValue}
        minValue={minDate}
        onChange={setDate}
      >
        <div className={styles.calendarHeader}>
          <Heading />
          <div className={styles.calendarNav}>
            <Button slot="previous" className={styles.calendarButton} onKeyDown={onKeyDown}>
              <ChevronLeftIcon height="10" strokeWidth="1.6" />
            </Button>
            <Button slot="next" className={styles.calendarButton} onKeyDown={onKeyDown}>
              <ChevronRightIcon height="10" strokeWidth="1.6" />
            </Button>
          </div>
        </div>
        <CalendarGrid weekdayStyle="short" className={styles.calendarGrid}>
          <CalendarGridHeader>
            {(day) => <CalendarHeaderCell>{day.slice(0, 2)}</CalendarHeaderCell>}
          </CalendarGridHeader>
          <CalendarGridBody>{(date) => <CalendarCell date={date} />}</CalendarGridBody>
        </CalendarGrid>
      </Calendar>
      <TimePicker value={timeValue} onChange={setTime} onKeyDown={onKeyDown} />
      <button className={styles.confirmButton} onClick={save} disabled={disabled}>
        <CircledCheckIcon width="17" height="17" />
      </button>
    </>
  );
}

const occasionToDate = (occasion: Occasion): CalendarDate =>
  parseDate(occasion.format(Occasion.parsableDateFormat));
const occasionToTime = (occasion: Occasion): Time =>
  parseTime(occasion.format(Occasion.parsableTimeFormat));

export default function DateTimePicker({
  dateTime,
  setDateTime,
  trigger,
  disabled = false,
  onClose,
  onOpen,
  defaultIsOpen = false,
  isOpen,
  setIsOpen,
  menuSide = 'auto',
  dateTimeFor = 'Reminder',
}: {
  dateTime: Occasion | null;
  setDateTime: (date: Occasion | null) => void;
  trigger: ReactNode;
  disabled?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  defaultIsOpen?: boolean;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  menuSide?: 'top' | 'bottom' | 'auto';
  // currently only used for setting Reminder dateTime -- if parent uses this for 'Reminder'
  // dateTime, we use the user's default Reminder time
  dateTimeFor?: 'Reminder';
}) {
  const defaultReminderTime = useSelector(selectDefaultReminderTime);
  let defaultHour = 8;
  let defaultMinute = 0;

  if (dateTimeFor === 'Reminder') {
    defaultHour = defaultReminderTime.hour;
    defaultMinute = defaultReminderTime.minute;
  }

  const now = useMemo(() => new Occasion(), []);
  const tomorrow = useMemo(() => {
    const { year, month, day } = now.dateParts;
    return new Occasion(new Date(year, month, day + 1, defaultHour, defaultMinute));
  }, []);
  const [date, setDate] = useState<CalendarDate>(occasionToDate(dateTime || tomorrow));
  const [time, setTime] = useState<Time>(occasionToTime(dateTime || tomorrow));
  const [menuIsOpen, setMenuIsOpen] = useState(defaultIsOpen);
  const [wasEdited, setWasEdited] = useState(false);
  const [timeInvalid, setTimeInvalid] = useState(false);
  const dateNow = useMemo(() => occasionToDate(now), []);
  const updatedDateTime = useMemo(
    () => new Occasion(toCalendarDateTime(date, time).toString()),
    [date, time],
  );
  const saveDisabled = useMemo(
    () => updatedDateTime.isBefore(now) || updatedDateTime.isEqual(dateTime),
    [updatedDateTime],
  );
  const _isOpen = isOpen ?? menuIsOpen;
  const _setIsOpen = setIsOpen ?? setMenuIsOpen;

  const save = () => {
    if (saveDisabled || timeInvalid || !wasEdited) {
      return;
    }

    setDateTime(new Occasion(updatedDateTime.toString()));
  };

  const handleDateChange = (date: CalendarDate) => {
    setDate(date);

    if (!wasEdited) {
      setWasEdited(true);
    }
  };

  const handleTimeChange = (time: Time) => {
    setTime(time);

    if (time) {
      setTimeInvalid(false);
    } else {
      setTimeInvalid(true);
    }

    if (!wasEdited) {
      setWasEdited(true);
    }
  };

  const saveAndClose = () => {
    save();
    onClose?.();
    _setIsOpen(false);
  };

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      saveAndClose();
    }

    if (metaTrigger(e) && e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      saveAndClose();
    }
  };

  useEffect(() => {
    setDate(occasionToDate(dateTime || tomorrow));
    setTime(occasionToTime(dateTime || tomorrow));
  }, []);

  return (
    <PopoverMenu
      trigger={trigger}
      menu={
        <PickerMenu
          dateValue={date}
          minDate={dateNow}
          setDate={handleDateChange}
          timeValue={time}
          setTime={handleTimeChange}
          save={saveAndClose}
          disabled={saveDisabled || timeInvalid}
          onKeyDown={onMenuKeyDown}
        />
      }
      menuClassName={styles.pickerMenu}
      disabled={disabled}
      onClose={saveAndClose}
      onOpen={onOpen}
      isOpen={_isOpen}
      setIsOpen={_setIsOpen}
      onMenuKeyDown={onMenuKeyDown}
      side={menuSide}
    />
  );
}
